import * as React from 'react';
import { useTheme } from '@emotion/react';

interface WorkspaceProjectItem {
  label: string;
  active?: boolean;
  opacity: number;
}

interface WorkspaceCodeLine {
  text: string;
  indent: number;
  opacity?: number;
}

interface OxideWorkspaceProps {
  build: number;
  live: number;
  sectionTitle: string;
  projectItems: readonly WorkspaceProjectItem[];
  fileName: string;
  codeLines: readonly WorkspaceCodeLine[];
  footerText: string;
}

/**
 * Renders the shared OXIDE editor frame. App and CloudCode variants compose their own file tree and editor content inside it.
 */
export const OxideWorkspace: React.FC<OxideWorkspaceProps> = ({
  build,
  live,
  sectionTitle,
  projectItems,
  fileName,
  codeLines,
  footerText
}) => {
  const theme = useTheme();
  const colors = theme.featureGraph;

  return (
    <g>
      <rect
        width="390"
        height="300"
        rx="16"
        fill={theme.surface.background}
        stroke={colors.interface.border}
        strokeWidth="2"
      />
      <rect width="390" height="54" rx="16" fill={colors.interface.background} />
      <path d="M0 54 H390" stroke={colors.interface.border} />
      <circle cx="20" cy="27" r="3" fill={colors.interface.accent} />
      <text x="34" y="34" fill={theme.text.heading} fontSize="22" fontWeight="600">
        OXIDE
      </text>
      <text x="370" y="31" textAnchor="end" fill={colors.interface.accent} fontSize="10" letterSpacing="1.5">
        {sectionTitle}
      </text>
      <path d="M108 54 V253 M0 253 H390" stroke={colors.interface.border} />
      <text x="18" y="85" fill={theme.text.paragraph} fontSize="9" letterSpacing="1.2">
        PROJECT
      </text>
      {projectItems.map((item, index) => (
        <g key={item.label} opacity={item.opacity}>
          <rect
            x="10"
            y={100 + index * 39}
            width="88"
            height="29"
            rx="5"
            fill={item.active ? colors.interface.background : theme.card.background}
          />
          <text
            x="20"
            y={119 + index * 39}
            fill={item.active ? colors.interface.accent : theme.text.paragraph}
            fontSize="12"
          >
            {item.label}
          </text>
        </g>
      ))}
      <text x="125" y="84" fill={theme.text.paragraph} fontSize="10">
        {fileName}
      </text>
      <g fill={colors.interface.accent} fontSize="11" fontFamily="monospace">
        {codeLines.map((line, index) => (
          <text key={line.text} x={125 + line.indent * 14} y={113 + index * 27} opacity={line.opacity ?? 1}>
            {line.text}
          </text>
        ))}
      </g>
      <circle cx="20" cy="276" r="4" fill={colors.feature.accent} opacity={live} />
      <text x="34" y="280" fill={theme.text.paragraph} fontSize="12">
        {footerText}
      </text>
    </g>
  );
};

export const OxideAppWorkspace: React.FC<{ build: number; live: number }> = ({ build, live }) => (
  <OxideWorkspace
    build={build}
    live={live}
    sectionTitle="BUILD YOUR APP"
    projectItems={['Views', 'Logic', 'Data model'].map((label, index) => ({
      label,
      active: index === 0,
      opacity: Math.max(0, Math.min(1, build * 3 - index))
    }))}
    fileName="inspection.view.xml"
    codeLines={[
      { text: '<view title="Inspection">', indent: 0 },
      { text: '<capture-photo />', indent: 1 },
      { text: '<toggle label="Checked" />', indent: 1 },
      { text: '<button label="Save" />', indent: 1 },
      { text: '</view>', indent: 0 }
    ]}
    footerText={live < 1 ? 'Views, logic, and data — together' : 'Your app, ready to run'}
  />
);

export const OxideCloudCodeWorkspace: React.FC<{ build: number; live: number }> = ({ build, live }) => (
  <OxideWorkspace
    build={build}
    live={live}
    sectionTitle="BUILD YOUR WORKFLOW"
    projectItems={['Views', 'CloudCode', 'Data model'].map((label, index) => ({
      label,
      active: index === 1,
      opacity: Math.max(0, Math.min(1, build * 3 - index))
    }))}
    fileName="generate-inspection-report"
    codeLines={[
      { text: 'export async function run() {', indent: 0, opacity: Math.max(0, Math.min(1, build * 5)) },
      { text: 'const inspection =', indent: 1, opacity: Math.max(0, Math.min(1, build * 5 - 1)) },
      { text: 'await fetchInspection();', indent: 2, opacity: Math.max(0, Math.min(1, build * 5 - 2)) },
      { text: 'await sendReport(inspection);', indent: 1, opacity: Math.max(0, Math.min(1, build * 5 - 3)) },
      { text: '}', indent: 0, opacity: Math.max(0, Math.min(1, build * 5 - 4)) }
    ]}
    footerText="CloudCode task · generate and send a report"
  />
);
